<template>
    <div>
        <div class="row">
            <div class="col-7">
                <h4>{{ $t('Unconfirmed bookings') }}</h4>
                <div class="table-responsive">
                    <table class="table table-striped table-sm">
                        <thead>
                            <tr>
                                <th>{{ $t('Pick up & return') }}</th>
                                <th>{{ $t('Price') }} & {{ $t('Vehicle') }}</th>
                                <th>{{ $t('Client') }}</th>
                                <th>{{ $t('Services') }} & {{ $t('Additional eq') }}</th>
                                <th width="75px"></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(booking, i) in bookings" :key="`booking_${i}`">
                                <td>
                                    <router-link :to="{name: 'booking.show', params: {booking_id: booking.id}}">
                                        <b>{{ booking.start_time | formatDateTime }} - {{ booking.end_time | formatDateTime }}</b>
                                        <br>
                                        <template v-if="booking.pickup_location_id === otherLocationId">
                                            <span class="mdi mdi-18px mdi-alert-outline text-danger"/> {{ booking.pickup_location_custom }}
                                        </template>
                                        <template v-else>
                                            {{ booking.pickup_location.name[locale] }}
                                        </template>
                                        <br>
                                        <template v-if="booking.return_location_id === otherLocationId">
                                            <span class="mdi mdi-18px mdi-alert-outline text-danger"/> {{ booking.return_location_custom }}
                                        </template>
                                        <template v-else>
                                            {{ booking.return_location.name[locale] }}
                                        </template>
                                        <template v-if="booking.travel_out">
                                            <br>
                                            <span class="mdi mdi-18px mdi-alert-outline text-danger"/> {{ $t('Travel out of Estonia') }}:
                                            <b>{{ booking.travel_out }}</b>
                                        </template>
                                    </router-link>
                                </td>
                                <td>
                                    <strong>{{ booking.prices.days }} x {{ booking.prices.day_price / 100 }}€ + {{ booking.prices.bond / 100 }}€</strong>
                                    <br>
                                    {{ booking.vehicle.make.name }} {{ booking.vehicle.model.name }}
                                    <app-vehicle-no :slug="booking.vehicle.slug"/>
                                    ,
                                    {{ booking.vehicle.reg_no }}
                                </td>
                                <td>
                                    {{ booking.fname }} {{ booking.lname }} <br>
                                    {{ booking.phone }} <br>
                                    {{ booking.email }} <br>
                                    {{ $t('Language') }}: {{ $t(langs.find(l => l.key === booking.lang).name) }}
                                    <template v-if="booking.promo">
                                        <br>
                                        <small>{{ $t('Promocode') }}</small> <br>
                                        <small>-{{ booking.promo.discount_percent }}%</small> "{{ booking.promo.code }}"
                                    </template>
                                    <template v-else-if="booking.client_id !== null">
                                        <br>
                                        <small>{{ $t('Client') }}</small> <br>
                                        <small>-{{ booking.client.discount_percent }}%</small> {{ booking.client.name }}
                                    </template>
                                </td>
                                <td>
                                    <template v-if="booking.services.length">
                                        <template v-for="(serv, i) in booking.services">
                                            {{ serv.name[locale] }}, <strong :key="`serv_${i}`">{{ (serv.price / 100).toFixed(2) }}€</strong><br
                                                :key="`serv_br_${i}`"/>
                                        </template>
                                    </template>
                                    <template v-if="booking.additional_eq.length">
                                        <hr>
                                        <template v-for="(eq, i) in booking.additional_eq">
                                            {{ eq.name[locale] }}, <strong
                                                :key="`eq_${i}`">{{ ((eq.all_prices.price + eq.all_prices.added_price) / 100).toFixed(2) }}€</strong><br
                                                :key="`eq_br_${i}`"/>
                                        </template>
                                    </template>
                                </td>
                                <td>
                                    <div class="text-right">
                                        <router-link v-if="!booking.contract"
                                                     :to="{name: 'booking.edit', params: {booking_id: booking.id}}"
                                                     class="btn btn-primary btn-xsm text-white">
                                            {{ $t('Edit') }}
                                        </router-link>
                                        <router-link v-else
                                                     :to="{name: 'contracts.show', params: {contract_id: booking.contract.id}}"
                                                     class="btn btn-info btn-xsm text-white">
                                            {{ $t('Contract') }}
                                        </router-link>
                                        <app-button native-type="button"
                                                    type="danger"
                                                    class="m-0"
                                                    @click="destroy(booking.id)"
                                                    :disabled="booking.confirmed_at !== null || booking.contract"
                                                    xsmall>
                                            {{ $t('Delete') }}
                                        </app-button>
                                        <template v-if="booking.confirmed_at">
                                            {{ booking.confirmed_at | formatDateTime }} <br>
                                            <a v-if="!booking.contract" class="btn-link text-danger" @click="confirmBooking(booking.id, false)">
                                                {{ $t('Unconfirm') }}
                                            </a>
                                        </template>
                                        <template v-else>
                                            <app-button native-type="button" type="success" @click="confirmBooking(booking.id, true)" xsmall>
                                                {{ $t('Confirm') }}
                                            </app-button>
                                        </template>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <div class="col-5">
                <h4>{{ $t('Pickups and returns') }}</h4>
                <div class="row">
                    <div class="col pt-2">
                        <app-datepicker v-model="today" @input="getPickupsReturns"/>
                    </div>
                    <div class="col text-right">
                        <app-button native-type="button" type="info" @click="showToday" small>
                            {{ $t('Today') }}
                        </app-button>
                        <app-button native-type="button" type="info" class="pt-1 pb-1" @click="subDay" small>
                            <span class="mdi mdi-18px mdi-arrow-left"/>
                        </app-button>
                        <app-button native-type="button" type="info" class="pt-1 pb-1" @click="addDay" small>
                            <span class="mdi mdi-18px mdi-arrow-right"/>
                        </app-button>
                    </div>
                </div>
                <div class="table-responsive">
                    <app-loader-gif v-if="loading"/>
                    <table v-if="pickups_returns.length && !loading" class="table table-striped table-sm">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th style="min-width: 288px">{{ $t('Pick up & return') }}</th>
                                <th>{{ $t('Vehicle') }}</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(item, i) in pickups_returns"
                                :key="i"
                                :title="item.return ? $t('Return') : $t('Pickup')"
                                :class="{'grayed-out':item.return ? item.booking.return_done_at : item.booking.pickup_done_at}">
                                <td>{{ i + 1 }}</td>
                                <td>
                                    <span v-if="item.return" class="mdi mdi-18px mdi-arrow-bottom-right-bold-outline text-success" :class="{'grayed-out':item.booking.return_done_at}"/>
                                    <span v-else class="mdi mdi-18px mdi-arrow-top-left-bold-outline text-danger" :class="{'grayed-out': isDone(item.return, item.booking)}"/>
                                    <span :class="{'font-weight-bold': !item.return}">
                                        {{ item.booking.start_time | formatDateTime }}
                                    </span>
                                    -
                                    <span :class="{'font-weight-bold': item.return}">
                                        {{ item.booking.end_time | formatDateTime }}
                                    </span> <br>
                                    <template v-if="item.booking.pickup_location_id === otherLocationId">
                                        <span class="mdi mdi-18px mdi-alert-outline text-danger" :class="{'grayed-out': isDone(item.return, item.booking)}"/> {{ item.booking.pickup_location_custom }}
                                    </template>
                                    <template v-else>
                                        <span :class="{'font-weight-bold': !item.booking.pickup_location.main_location}">
                                            {{ item.booking.pickup_location.name[locale] }}
                                        </span>
                                    </template>
                                    <br>
                                    <template v-if="item.booking.return_location_id === otherLocationId">
                                        <span class="mdi mdi-18px mdi-alert-outline text-danger" :class="{'grayed-out': isDone(item.return, item.booking)}"/> {{ item.booking.return_location_custom }}
                                    </template>
                                    <template v-else>
                                        <span :class="{'font-weight-bold': !item.booking.return_location.main_location}">
                                            {{ item.booking.return_location.name[locale] }}
                                        </span>
                                    </template>
                                    <template v-if="item.booking.travel_out">
                                        <br>
                                        <span class="mdi mdi-18px mdi-alert-outline text-danger" :class="{'grayed-out': isDone(item.return, item.booking)}"/> {{ $t('Travel out of Estonia') }}:
                                        <b>{{ item.booking.travel_out }}</b>
                                    </template>
                                </td>
                                <td>
                                    {{ item.booking.vehicle.make.name }} {{ item.booking.vehicle.model.name }}
                                    <app-vehicle-no :slug="item.booking.vehicle.slug"/>, {{ item.booking.vehicle.reg_no }}
                                    <br>
                                    {{ item.booking.fname }} {{ item.booking.lname }}, {{ item.booking.phone }} <br>
                                    {{ item.booking.prices.days }} x {{ item.booking.prices.day_price / 100 }}€ + {{ item.booking.prices.bond / 100 }}€
                                </td>
                                <td>
                                    <div class="d-flex justify-content-end">
                                        <div v-if="item.return" class="custom-control custom-switch">
                                            <input type="checkbox"
                                                   class="custom-control-input"
                                                   :id="`return_done_at_${i}`"
                                                   :checked="item.booking.return_done_at || ''"
                                                   @change="toggleDone(item.booking.id, true, i)">
                                            <label class="custom-control-label" :for="`return_done_at_${i}`"/>
                                        </div>
                                        <div v-else class="custom-control custom-switch">
                                            <input type="checkbox"
                                                   class="custom-control-input"
                                                   :id="`pickup_done_at_${i}`"
                                                   :checked="item.booking.pickup_done_at || ''"
                                                   @change="toggleDone(item.booking.id, false, i)">
                                            <label class="custom-control-label" :for="`pickup_done_at_${i}`"/>
                                        </div>
                                        <router-link v-if="item.booking.contract && $store.getters.canViewContracts"
                                                     :to="{name: 'contracts.show', params: {contract_id: item.booking.contract.id}}"
                                                     class="btn btn-info btn-xsm text-white">
                                            {{ $t('Contract') }}
                                        </router-link>
                                        <router-link :to="{name: 'booking.show', params: {booking_id: item.id}}" class="btn btn-info btn-xsm text-white">
                                            {{ $t('View') }}
                                        </router-link>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import BookingService from '@services/booking.service';
import {mapGetters} from 'vuex';
import Vue from 'vue';
import moment from 'moment';
import {fixDate} from '@services/helper.service';

export default {
    data() {
        return {
            today: fixDate(new Date()),
            pickups_returns: [],
            bookings: [],
            loading: false
        };
    },
    computed: {
        ...mapGetters(['locale', 'langs', 'otherLocationId', 'localeDateFormat']),
    },
    methods: {
        isDone(isReturn, booking) {
            if (isReturn) {
                return booking.return_done_at;
            }
            return booking.pickup_done_at;
        },
        toggleDone(bookingId, isReturn, index) {
            BookingService.toggleDone(bookingId, isReturn).then(res => {
                if (isReturn) {
                    Vue.set(this.pickups_returns[index].booking, 'return_done_at', res.return_done_at);
                } else {
                    Vue.set(this.pickups_returns[index].booking, 'pickup_done_at', res.pickup_done_at);
                }
                this.$toastr.s(this.$t('Updated'));
            });
        },
        showToday() {
            this.today = moment(new Date()).format('L');
            this.getPickupsReturns();
        },
        addDay() {
            this.today = moment(this.today, this.localeDateFormat).add(1, 'day').format('L');
            this.getPickupsReturns();
        },
        subDay() {
            this.today = moment(this.today, this.localeDateFormat).subtract(1, 'day').format('L');
            this.getPickupsReturns();
        },
        getBookings() {
            BookingService.notConfirmed().then(res => this.bookings = res);
        },
        getPickupsReturns() {
            this.loading = true;
            BookingService.pickupsReturns(this.today).then(res => {
                this.pickups_returns = res;
                this.loading = false;
            });
        },
        confirmBooking(bookingId, confirm) {
            let msg = this.$t('Unconfirm');
            if (confirm) {
                msg = this.$t('Confirm');
            }
            if (!window.confirm(`${msg}?`)) {
                return false;
            }
            BookingService.confirmBooking(bookingId).then(res => {
                const index = this.bookings.findIndex(b => b.id === bookingId);
                Vue.set(this.bookings, index, res);
                this.$toastr.s(this.$t('Updated'));
            });
        },
        destroy(bookingId) {
            if (!window.confirm(`${this.$t('Delete')}?`)) {
                return false;
            }
            BookingService.destroy(bookingId).then(res => {
                const index = this.bookings.findIndex(b => b.id === bookingId);
                this.bookings.splice(index, 1);
                this.$toastr.s(this.$t('Deleted'));
            });
        }
    },
    created() {
        this.getBookings();
        this.getPickupsReturns();
    }
};
</script>
<style scoped>
.grayed-out {
    background: #f5f8ff !important;
    color: #dee2e6 !important;
}
</style>
